import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Input,
  Link,
  Stack,
  TextField,
} from '@material-ui/core'
import AddBoxIcon from '@material-ui/icons/AddBox'
import DeleteIcon from '@material-ui/icons/Delete'
import { makeStyles } from '@material-ui/styles';
import { Fragment, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { setError } from 'src/redux/errorReducer'
import { getFilePreview, validateAudio } from 'src/util/helpers';

import WatchVideoOrImageField from './Fields/WatchVideoOrImageField';

const getInitialRowsCount = obj =>
  Object.keys(obj).filter(
    key =>
      key.startsWith('option') &&
      key !== 'optiontype' &&
      !key.toLowerCase().includes('nativeoption') &&
      !key.toLowerCase().includes('file')
  );

const getInitialOptions = childData => {
  const totalOptions = Math.max(getInitialRowsCount(childData).length, 1);

  return Array.from({ length: totalOptions }).reduce((options, _, index) => {
    const keyIndex = index + 1;
    return {
      ...options,
      [`option${keyIndex}`]: childData[`option${keyIndex}`] || '',
      [`nativeOption${keyIndex}`]: childData[`nativeOption${keyIndex}`] || '',
      [`option${keyIndex}File`]: childData[`option${keyIndex}File`] || ''
    };
  }, {
    audio: childData.audio || '',
    audioSlow: childData.audioSlow || '',
    answer: childData.answer || '',
    isImage: childData.isImage || false,
    exerciseImage: childData.exerciseImage || '',
    exerciseVideo: childData.exerciseVideo || '',
    video: childData.video || '',
  });
};


// Note: This form covers 2 use cases "Listen and choose right order of text" and "Watch and choose right order of text"
const RightOrderOfText = ({
  numberOfRows = 1,
  hasVideo = false,
  onDataChange,
  onMediaRemove,
  childData,
  isDisabled,
  fileKeys = {},
}) => {
  const useStyles = makeStyles({
    link: {
      cursor: 'pointer'
    }
  });

  const dispatch = useDispatch();
  const classes = useStyles();
  const [rows, setRows] = useState(numberOfRows)
  const [values, setValues] = useState(getInitialOptions(childData))

  useEffect(() => {
    setRows(getInitialRowsCount(childData)?.length || numberOfRows);
    setValues(getInitialOptions(childData))
  }, [childData])

  const addRows = () => {
    setRows(rows + 1);
    let updatedRowsData = {
      ...values,
      [`option${rows + 1}`]: childData[`option${rows + 1}`] || '',
      [`nativeOption${rows + 1}`]: childData[`nativeOption${rows + 1}`] || '',
      [`option${rows + 1}File`]: childData[`option${rows + 1}File`] || ''
    }
    onDataChange(updatedRowsData)
  }

  const changeHandler = ({ target: { name, value, files, checked } }, fieldLabel) => {
    let finalValues
    if (files?.length) {
      let file = files[0]
      if (fieldLabel?.toLowerCase()?.includes('audio')) {
        const error = validateAudio(file)
        if (error) {
          alert(error)
          return // Don't proceed if validation fails
        }
      }

      file.fieldLabel = fieldLabel
      finalValues = {
        ...values,
        [name]: file,
      }
    } else {
      finalValues = {
        ...values,
        [name]: name === 'isImage' ? checked : value,
      }
    }
    setValues(finalValues)
    onDataChange(finalValues)
  }

  const onRemoveRowButtonClick = async (rowIndex) => {
    if (rows === 1) {
      dispatch(setError('Options must not be less than 1.'))
      return
    }
    const updatedRowsData = { ...values };

    let totalOptions = 0;
    while (updatedRowsData[`option${totalOptions + 1}`] !== undefined) {
      totalOptions++;
    }

    for (let i = rowIndex; i < totalOptions; i++) {
      updatedRowsData[`option${i}`] = updatedRowsData[`option${i + 1}`] || "";
      updatedRowsData[`nativeOption${i}`] = updatedRowsData[`nativeOption${i + 1}`] || "";
      updatedRowsData[`option${i}File`] = updatedRowsData[`option${i + 1}File`] || "";
    }

    delete updatedRowsData[`option${totalOptions}`];
    delete updatedRowsData[`nativeOption${totalOptions}`];
    delete updatedRowsData[`option${totalOptions}File`];
    await onDataChange(getInitialOptions(updatedRowsData))
    setRows(rows - 1);
  }

  return (
    <Box sx={{ p: 2, border: '1px solid grey' }}>
      <FormGroup>
        <WatchVideoOrImageField
          isImage={values.isImage}
          onChange={e => changeHandler(e)}
          isDisabled={isDisabled}
          value={values.isImage ? values.exerciseImage : values.exerciseVideo}
          fileKeys={fileKeys}
        />
        {hasVideo ? (
          <Stack direction="row" spacing={2} margin={1}>
            <TextField
              fullWidth
              label="Video Url"
              name={'video'}
              onChange={changeHandler}
              required
              value={values?.video}
              variant="outlined"
            />
          </Stack>
        ) : (
          <Fragment>
            <Stack direction="row" spacing={2} margin={1}>
              <Grid direction="row" container spacing={1}>
                <Grid item md={3} xs={3}>
                  <FormControlLabel
                    label={''}
                    htmlFor={`audio`}
                    control={
                      <>
                        <Input
                          key={fileKeys['audio']}
                          style={{ display: 'none' }}
                          onChange={event => changeHandler(event, 'Audio')}
                          name={`audio`}
                          inputProps={{
                            accept: 'audio/mp3',
                          }}
                          id={`audio`}
                          type="file"
                        // disabled={!!values?.audio}
                        />
                        <Button variant="contained" component="span">
                          {'Audio'}
                        </Button>
                      </>
                    }
                  ></FormControlLabel>
                </Grid>
                <Grid item md={8} xs={8}>
                  {getFilePreview(values[`audio`])}
                </Grid>
                <Grid item md={1} xs={1}>
                  {values[`audio`] && (
                    <Link className={classes.link} onClick={() => onMediaRemove('audio', values)}>Clear</Link>
                  )}
                </Grid>
              </Grid>
            </Stack>
            <Divider key="audioDivider" />
            <Stack direction="row" spacing={2} margin={1}>
              <Grid direction="row" container spacing={1}>
                <Grid item md={3} xs={3}>
                  <FormControlLabel
                    label={''}
                    htmlFor={`audioSlow`}
                    control={
                      <>
                        <Input
                          key={fileKeys['audioSlow']}
                          style={{ display: 'none' }}
                          onChange={event => changeHandler(event, 'audioSlow')}
                          name={`audioSlow`}
                          inputProps={{
                            accept: 'audio/mpeg, audio/mp3',
                          }}
                          id={`audioSlow`}
                          type="file"
                        // disabled={!!values?.audioSlow}
                        />
                        <Button variant="contained" component="span">
                          {'Slow_Audio'}
                        </Button>
                      </>
                    }
                  ></FormControlLabel>
                </Grid>
                <Grid item md={8} xs={8}>
                  {getFilePreview(values[`audioSlow`])}
                </Grid>
                <Grid item md={1} xs={1}>
                  {values[`audioSlow`] && (
                    <Link className={classes.link} onClick={() => onMediaRemove('audioSlow', values)}>Clear</Link>
                  )}
                </Grid>
              </Grid>
            </Stack>
            <Divider key="slowAudioDivider" />
          </Fragment>
        )}
        <Stack direction="row" spacing={2} margin={1}>
          <TextField
            fullWidth
            label="Answer"
            name={'answer'}
            onChange={changeHandler}
            required
            value={values?.answer}
            variant="outlined"
          />
        </Stack>
        {[...Array(rows)].map((el, index) => (
          <Fragment key={index + 'Fragement'}>
            <Stack
              key={index + 'row'}
              direction="column"
              spacing={2}
              margin={1}
            >
              <Grid direction="row" container spacing={1}>
                <Grid item md={3} xs={3}>
                  <FormControlLabel
                    label={'Option' + (index + 1)}
                    control={<></>}
                    htmlFor="answer"
                  ></FormControlLabel>
                </Grid>
                <Grid item md={4} xs={4}>
                  <TextField
                    fullWidth
                    label="Words"
                    name={'option' + (index + 1)}
                    onChange={changeHandler}
                    required
                    value={values && values['option' + (index + 1)]}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={4} xs={4}>
                  <TextField
                    fullWidth
                    label="Native Words"
                    name={'nativeOption' + (index + 1)}
                    onChange={changeHandler}
                    required
                    value={values && values['nativeOption' + (index + 1)]}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={1} xs={1}>
                  <IconButton
                    size="large"
                    onClick={() => onRemoveRowButtonClick(index + 1)}
                  >
                    <DeleteIcon fontSize="medium" />
                  </IconButton>
                </Grid>
              </Grid>
              <Grid direction="row" container spacing={1}>
                <Grid item md={4} xs={4}>
                  <FormControlLabel
                    label={''}
                    htmlFor={`option${index + 1}File`}
                    control={
                      <>
                        <Input
                          key={values[`option${index + 1}File`]?.name || fileKeys[`option${index + 1}File`]}
                          style={{ display: 'none' }}
                          onChange={event =>
                            changeHandler(event, 'Audio' + (index + 1))
                          }
                          name={`option${index + 1}File`}
                          inputProps={{
                            accept: 'audio/mpeg, audio/mp3',
                          }}
                          id={`option${index + 1}File`}
                          type="file"
                        // disabled={!!values && values[`option${index + 1}File`]}
                        />
                        <Button variant="contained" component="span">
                          {'Audio' + (index + 1)}
                        </Button>
                      </>
                    }
                  ></FormControlLabel>
                </Grid>
                <Grid item md={7} xs={7}>
                  {getFilePreview(values[`option${index + 1}File`])}
                </Grid>
                <Grid item md={1} xs={1}>
                  {values[`option${index + 1}File`] && (
                    <Link className={classes.link} onClick={() => onMediaRemove(`option${index + 1}File`, values)}>Clear</Link>
                  )}
                </Grid>
              </Grid>
            </Stack>
            <Divider key={index + 'Divider'} />
          </Fragment>
        ))}
        <Grid direction="row" container spacing={1} justifyContent={'flex-end'}>
          <Grid item>
            <IconButton size="large" onClick={addRows}>
              <AddBoxIcon fontSize={'large'} />
            </IconButton>
          </Grid>
        </Grid>
      </FormGroup>
    </Box>
  )
}

export default RightOrderOfText
